import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  Search,
  Eye,
  Trash2,
  CheckCircle2,
  X,
  Reply,
  Archive,
  User,
  Calendar,
  Clock,
  Phone,
  FileText,
  Loader2,
} from "lucide-react";

const API_BASE = "/api";

const services = ["All", "Boundary Survey", "Topographic Survey", "Construction Survey", "GIS Services", "Subdivision Survey", "Cadastral Survey", "Other"];
const statuses = ["All", "New", "Replied", "Archived"];

interface Message {
  id: number;
  name: string;
  email: string;
  phone: string;
  service: string;
  message: string;
  status: string;
  read: boolean;
  date: string;
  time: string;
  created_at: string;
}

export default function AdminMessages() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [serviceFilter, setServiceFilter] = useState("All");
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const [showNotification, setShowNotification] = useState<string | null>(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE}/messages`, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` } });
      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.error || "Failed to fetch messages");
      }

      if (!Array.isArray(data)) {
        throw new Error("Invalid response format");
      }

      const formatted = data.map((m: Message) => ({
        ...m,
        date: m.created_at ? new Date(m.created_at).toLocaleDateString() : "-",
        time: m.created_at
          ? new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
          : "-",
        read: m.read === true,
      }));

      setMessages(formatted);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredMessages = messages.filter((message) => {
    const matchesSearch =
      message.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      message.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      message.message?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "All" || message.status === statusFilter;
    const matchesService = serviceFilter === "All" || message.service === serviceFilter;
    return matchesSearch && matchesStatus && matchesService;
  });

  const unreadCount = messages.filter((m) => !m.read).length;
  const newCount = messages.filter((m) => m.status === "New").length;

  const handleMarkAsRead = (id: number) => {
    setMessages(messages.map((m) => (m.id === id ? { ...m, read: true } : m)));
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      const response = await fetch(`${API_BASE}/messages/${id}`, {
        method: "PUT",
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        setMessages(messages.map((m) => (m.id === id ? { ...m, status: newStatus } : m)));
        setShowNotification(`Marked as ${newStatus}`);
        setTimeout(() => setShowNotification(null), 3000);
      }
    } catch {
      // Silent fail
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await fetch(`${API_BASE}/messages/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` },
      });

      if (!response.ok) {
        throw new Error(`Delete failed: HTTP ${response.status}`);
      }

      setMessages(messages.filter((m) => m.id !== id));
      setShowDeleteConfirm(null);
      setSelectedMessage(null);
      setShowNotification("Message deleted");
      setTimeout(() => setShowNotification(null), 3000);
    } catch {
      setShowNotification("Failed to delete message");
      setTimeout(() => setShowNotification(null), 3000);
    }
  };

  const handleView = (message: Message) => {
    setSelectedMessage(message);
    if (!message.read) {
      handleMarkAsRead(message.id);
    }
  };

  const handleReply = (message: Message) => {
    const params = new URLSearchParams({
      compose: "1",
      to: message.email || "",
      subject: `Re: ${message.service || "Your message to Bomtrack"}`,
    });
    navigate(`/admin/email?${params.toString()}`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "New":
        return "bg-blue-500/10 text-blue-600";
      case "Replied":
        return "bg-green-500/10 text-green-600";
      case "Archived":
        return "bg-neutral-500/10 text-neutral-500";
      default:
        return "bg-neutral-100 text-neutral-600";
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="w-8 h-0.5 bg-accent rounded-full" />
            <span className="text-accent text-xs font-medium uppercase tracking-wide">Inbox</span>
          </div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-primary">Messages</h1>
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 bg-accent/10 text-accent text-xs font-medium rounded-full">
                {unreadCount} unread
              </span>
            )}
          </div>
          <p className="text-neutral-400 text-sm mt-1">Manage contact form submissions and inquiries</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-blue-500/10 rounded-xl">
            <Mail className="w-4 h-4 text-blue-500" />
            <span className="text-sm font-medium text-blue-600">{newCount} New</span>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-red-600 text-sm">
          <div className="font-semibold mb-1">Error loading messages</div>
          <div>{error}</div>
        </div>
      )}

      {/* Notification */}
      <AnimatePresence>
        {showNotification && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-green-50 border border-green-100 rounded-xl p-3 sm:p-4 flex items-center gap-2 sm:gap-3"
          >
            <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
            <span className="text-green-600 text-xs sm:text-sm">{showNotification}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-neutral-100 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/10 rounded-xl flex items-center justify-center">
              <Mail className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">{messages.length}</div>
              <div className="text-xs text-neutral-400">Total Messages</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-neutral-100 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-accent/10 rounded-xl flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-accent" />
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">{newCount}</div>
              <div className="text-xs text-neutral-400">New</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-neutral-100 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500/10 rounded-xl flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <div className="text-2xl font-bold text-primary">
                {messages.filter((m) => m.status === "Replied").length}
              </div>
              <div className="text-xs text-neutral-400">Replied</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-neutral-100 p-5">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <input
              type="text"
              placeholder="Search messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
            />
          </div>
          <div className="flex gap-2 sm:gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full sm:w-auto px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm appearance-none bg-white"
            >
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
            <select
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              className="flex-1 sm:flex-none px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm appearance-none bg-white"
            >
              {services.map((service) => (
                <option key={service} value={service}>
                  {service}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-accent animate-spin" />
        </div>
      )}

      {/* Messages Table / Cards */}
      {!loading && (
        <div className="bg-white rounded-xl border border-neutral-100 overflow-hidden">
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-neutral-100 bg-neutral-50/50">
                  <th className="text-left py-3 px-4 font-medium text-neutral-600 text-sm">Sender</th>
                  <th className="text-left py-3 px-4 font-medium text-neutral-600 text-sm">Service</th>
                  <th className="text-left py-3 px-4 font-medium text-neutral-600 text-sm">Message Preview</th>
                  <th className="text-left py-3 px-4 font-medium text-neutral-600 text-sm">Date</th>
                  <th className="text-left py-3 px-4 font-medium text-neutral-600 text-sm">Status</th>
                  <th className="text-right py-3 px-4 font-medium text-neutral-600 text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMessages.map((message) => (
                  <tr
                    key={message.id}
                    className={`border-b border-neutral-50 hover:bg-neutral-50/50 transition-colors ${
                      !message.read ? "bg-blue-50/30" : ""
                    }`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                            !message.read ? "bg-accent/10" : "bg-neutral-100"
                          }`}
                        >
                          <User className={`w-4 h-4 ${!message.read ? "text-accent" : "text-neutral-400"}`} />
                        </div>
                        <div>
                          <div
                            className={`text-sm ${
                              !message.read ? "font-semibold text-primary" : "font-medium text-neutral-700"
                            }`}
                          >
                            {message.name}
                          </div>
                          <div className="text-xs text-neutral-400">{message.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-xs text-neutral-500 bg-neutral-100 px-2 py-1 rounded-lg">
                        {message.service}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-sm text-neutral-500 truncate max-w-xs">{message.message}</p>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm text-neutral-500">{message.date}</div>
                      <div className="text-xs text-neutral-400">{message.time}</div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(message.status)}`}>
                        {message.status}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleReply(message)}
                          className="p-1.5 hover:bg-accent/10 rounded-lg text-neutral-400 hover:text-accent transition-colors"
                          title="Reply by email"
                        >
                          <Reply className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleView(message)}
                          className="p-1.5 hover:bg-neutral-100 rounded-lg text-neutral-400 hover:text-primary transition-colors"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() =>
                            handleStatusChange(message.id, message.status === "Archived" ? "New" : "Archived")
                          }
                          className="p-1.5 hover:bg-neutral-100 rounded-lg text-neutral-400 hover:text-accent transition-colors"
                          title={message.status === "Archived" ? "Unarchive" : "Archive"}
                        >
                          <Archive className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(message.id)}
                          className="p-1.5 hover:bg-red-50 rounded-lg text-neutral-400 hover:text-red-500 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden">
            {filteredMessages.map((message) => (
              <div
                key={message.id}
                onClick={() => handleView(message)}
                className={`p-4 border-b border-neutral-100 cursor-pointer active:bg-neutral-50 ${
                  !message.read ? "bg-blue-50/30" : ""
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      !message.read ? "bg-accent/10" : "bg-neutral-100"
                    }`}
                  >
                    <User className={`w-5 h-5 ${!message.read ? "text-accent" : "text-neutral-400"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span
                        className={`text-sm truncate ${
                          !message.read ? "font-semibold text-primary" : "font-medium text-neutral-700"
                        }`}
                      >
                        {message.name}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${getStatusColor(
                          message.status
                        )}`}
                      >
                        {message.status}
                      </span>
                    </div>
                    <div className="text-xs text-neutral-400 mb-1">{message.email}</div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs text-neutral-500 bg-neutral-100 px-2 py-0.5 rounded">
                        {message.service}
                      </span>
                      <span className="text-xs text-neutral-400">{message.date}</span>
                    </div>
                    <p className="text-sm text-neutral-500 mt-2 line-clamp-2">{message.message}</p>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-neutral-100">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReply(message);
                    }}
                    className="px-3 py-1.5 text-xs font-medium text-accent hover:bg-accent/10 rounded-lg transition-colors flex items-center gap-1"
                  >
                    <Reply className="w-3.5 h-3.5" />
                    Reply
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStatusChange(message.id, message.status === "Archived" ? "New" : "Archived");
                    }}
                    className="px-3 py-1.5 text-xs font-medium text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors flex items-center gap-1"
                  >
                    <Archive className="w-3.5 h-3.5" />
                    {message.status === "Archived" ? "Unarchive" : "Archive"}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDeleteConfirm(message.id);
                    }}
                    className="px-3 py-1.5 text-xs font-medium text-red-500 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {filteredMessages.length === 0 && !loading && (
            <div className="text-center py-12">
              <div className="w-14 h-14 bg-neutral-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <Mail className="w-7 h-7 text-neutral-300" />
              </div>
              <h3 className="text-base font-medium text-neutral-600 mb-1">No messages found</h3>
              <p className="text-neutral-400 text-sm">Contact form submissions will appear here</p>
            </div>
          )}
        </div>
      )}

      {/* View Message Modal */}
      <AnimatePresence>
        {selectedMessage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-0 sm:p-4"
            onClick={() => setSelectedMessage(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              className="bg-white sm:rounded-2xl w-full max-w-2xl h-full sm:h-auto sm:max-h-[90vh] overflow-hidden shadow-2xl"
            >
              <div className="flex items-center justify-between p-4 sm:p-5 border-b border-neutral-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-accent/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-accent" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-base sm:text-lg font-semibold text-primary truncate">
                      {selectedMessage.name}
                    </h2>
                    <p className="text-xs sm:text-sm text-neutral-400 truncate">{selectedMessage.email}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedMessage(null)}
                  className="p-2 hover:bg-neutral-100 rounded-xl transition-colors flex-shrink-0"
                >
                  <X className="w-5 h-5 text-neutral-400" />
                </button>
              </div>

              <div className="p-4 sm:p-5 overflow-y-auto max-h-[calc(100vh-180px)] sm:max-h-[calc(90vh-180px)]">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-5">
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                    <span className="text-neutral-600">{selectedMessage.phone || "N/A"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                    <span className="text-neutral-600">{selectedMessage.service}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                    <span className="text-neutral-600">{selectedMessage.date}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                    <span className="text-neutral-600">{selectedMessage.time}</span>
                  </div>
                </div>

                <div className="mb-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedMessage.status)}`}>
                    {selectedMessage.status}
                  </span>
                </div>

                <div className="bg-neutral-50 rounded-xl p-3 sm:p-4">
                  <h3 className="text-sm font-medium text-neutral-700 mb-2">Message</h3>
                  <p className="text-neutral-600 text-sm leading-relaxed">{selectedMessage.message}</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-4 sm:p-5 border-t border-neutral-100">
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => handleReply(selectedMessage)}
                    className="px-4 py-2 rounded-xl bg-accent hover:bg-accent/90 text-white transition-all shadow-lg shadow-accent/20 text-sm font-medium flex items-center justify-center gap-2"
                  >
                    <Reply className="w-4 h-4" />
                    Reply by Email
                  </button>
                  <button
                    onClick={() => {
                      handleStatusChange(selectedMessage.id, "Replied");
                      setSelectedMessage(null);
                    }}
                    className="px-4 py-2 rounded-xl border border-neutral-200 text-neutral-600 hover:bg-neutral-50 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                  >
                    <Reply className="w-4 h-4" />
                    Mark as Replied
                  </button>
                  <button
                    onClick={() => {
                      handleStatusChange(
                        selectedMessage.id,
                        selectedMessage.status === "Archived" ? "New" : "Archived"
                      );
                      setSelectedMessage(null);
                    }}
                    className="px-4 py-2 rounded-xl border border-neutral-200 text-neutral-600 hover:bg-neutral-50 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                  >
                    <Archive className="w-4 h-4" />
                    {selectedMessage.status === "Archived" ? "Unarchive" : "Archive"}
                  </button>
                </div>
                <button
                  onClick={() => {
                    setShowDeleteConfirm(selectedMessage.id);
                    setSelectedMessage(null);
                  }}
                  className="px-4 py-2 rounded-xl text-red-500 hover:bg-red-50 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3 sm:p-4"
            onClick={() => setShowDeleteConfirm(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              className="bg-white rounded-2xl p-5 sm:p-6 max-w-sm w-full shadow-2xl"
            >
              <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-500" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-primary text-center mb-2">
                Delete Message?
              </h3>
              <p className="text-neutral-500 text-sm text-center mb-5 sm:mb-6">
                This action cannot be undone. The message will be permanently removed.
              </p>
              <div className="flex gap-2 sm:gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 px-3 sm:px-4 py-2 rounded-xl border border-neutral-200 text-neutral-600 hover:bg-neutral-50 transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(showDeleteConfirm)}
                  className="flex-1 px-3 sm:px-4 py-2 rounded-xl bg-red-500 hover:bg-red-600 text-white transition-colors text-sm font-medium"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
